var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SftpImportConfig_1;
/* eslint-disable class-methods-use-this */
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ImportOperation } from '../importTask';
import { ImportConfig, ImportConfigType } from './importConfig';
import './dataPreprocessConfig';
import './fwfColumnConfiguration';
import { sftpImportConfigSchemaValidation } from '../../validations.generated';
// tap-sftp uses the file-processors library to support multiple filetypes
export var SftpSupportedType;
(function (SftpSupportedType) {
    SftpSupportedType["Csv"] = ".csv";
    SftpSupportedType["Excel"] = ".xls";
    SftpSupportedType["ExcelX"] = ".xlsx";
    SftpSupportedType["Text"] = ".txt";
    SftpSupportedType["Fwf"] = ".fwf";
})(SftpSupportedType || (SftpSupportedType = {}));
export var SftpEncryptedSupportedType;
(function (SftpEncryptedSupportedType) {
    // no longer suitable for tap-sftp > v2.1.18
    SftpEncryptedSupportedType["pgp"] = ".pgp";
    SftpEncryptedSupportedType["gpg"] = ".gpg";
})(SftpEncryptedSupportedType || (SftpEncryptedSupportedType = {}));
export var TapSftpFileType;
(function (TapSftpFileType) {
    // Map SftpSupportedType to file_type for tap-sftp config.json
    // tap-sftp > v2.1.18 only accepts: 'csv', 'excel' or 'text'
    TapSftpFileType["Csv"] = "csv";
    TapSftpFileType["Excel"] = "excel";
    TapSftpFileType["ExcelX"] = "excel";
    TapSftpFileType["Text"] = "text";
    TapSftpFileType["Fwf"] = "fwf";
})(TapSftpFileType || (TapSftpFileType = {}));
export var DynamicImportSupportedValues;
(function (DynamicImportSupportedValues) {
    DynamicImportSupportedValues["OldestModified"] = "oldestModified";
    DynamicImportSupportedValues["NewestModified"] = "newestModified";
})(DynamicImportSupportedValues || (DynamicImportSupportedValues = {}));
export const sftpImportConfigSchema = {
    $id: 'urn:wisepipe-common-data:SftpImportConfig',
    type: 'object',
    properties: {
        filepath: { type: 'string' },
        filename: { type: 'string' },
        prefix: { type: 'string', minLength: 0 }, // empty prefix is ok
        filetype: { type: 'string', enum: Object.values({ ...SftpSupportedType, ...SftpEncryptedSupportedType }) },
        useEncryption: { type: 'boolean' },
        delimiter: { type: 'string', minLength: 1, maxLength: 1 },
        quotechar: { type: 'string', minLength: 1, maxLength: 1 },
        hasHeader: { type: 'boolean' },
        dataPreprocessConfig: { $ref: 'urn:wisepipe-common-data:DataPreprocessConfig' },
        columnConfiguration: { $ref: 'urn:wisepipe-common-data:FwfColumnConfiguration' },
        dynamic: { type: 'string', enum: Object.values(DynamicImportSupportedValues) }
    },
    required: ['filepath', 'filename', 'prefix', 'filetype']
};
let SftpImportConfig = SftpImportConfig_1 = class SftpImportConfig extends ImportConfig {
    constructor(filepath, filetype, useEncryption = false, delimiter, quotechar, hasHeader, dataPreprocessConfig, columnsToUpdate, columnConfiguration, dynamic, searchPattern) {
        super(ImportConfigType.Sftp, columnsToUpdate);
        this.filepath = filepath;
        this.filetype = filetype;
        this.useEncryption = useEncryption;
        this.delimiter = delimiter;
        this.quotechar = quotechar;
        this.hasHeader = hasHeader;
        this.dataPreprocessConfig = dataPreprocessConfig;
        this.columnConfiguration = columnConfiguration;
        this.dynamic = dynamic;
        this.filename = this.dynamic ? searchPattern ?? '' : this.getFileName();
        this.prefix = this.getPrefix();
        JSON.validate(this, SftpImportConfig_1);
    }
    get supportedRefreshOperations() {
        return [ImportOperation.Overwrite];
    }
    get needsDiscovery() {
        return true;
    }
    get fileTypeSupportsTargetParquet() {
        return ['.csv', '.txt', '.fwf'].includes(this.filetype);
    }
    get fileTypeSupportsDataPreprocess() {
        return ['.csv', '.txt', '.fwf'].includes(this.filetype);
    }
    get isFileTypeCsv() {
        // for backwards compatibility. pgp, gpg only supported for csv type in prev release
        if (Object.values(SftpEncryptedSupportedType).includes(this.filetype)) {
            return true;
        }
        return ['.csv', '.txt'].includes(this.filetype);
    }
    get tapSftpFileType() {
        // Map SftpImportConfig.filetype to the tap-sftp file_type in config.json
        switch (this.filetype) {
            case SftpSupportedType.Csv:
                return TapSftpFileType.Csv.toString();
            case SftpSupportedType.Excel:
                return TapSftpFileType.Excel.toString();
            case SftpSupportedType.ExcelX:
                return TapSftpFileType.ExcelX.toString();
            case SftpSupportedType.Text:
                return TapSftpFileType.Text.toString();
            case SftpSupportedType.Fwf:
                return TapSftpFileType.Fwf.toString();
            // for backwards compatibility
            case SftpEncryptedSupportedType.pgp:
            case SftpEncryptedSupportedType.gpg:
                return TapSftpFileType.Csv.toString();
            default:
                throw new Error(`Unrecognized filetype: ${this.filetype}`);
        }
    }
    get shouldTargetCleanData() {
        return true;
    }
    getFileName() {
        return this.filepath.split('/').pop();
    }
    getPrefix() {
        if (this.dynamic)
            return this.filepath;
        const splitFile = this.filepath.split('/');
        // discard file name
        splitFile.pop();
        const prefix = splitFile.join('/');
        return prefix;
    }
};
SftpImportConfig = SftpImportConfig_1 = __decorate([
    JsonSchemaV2(sftpImportConfigSchema, sftpImportConfigSchemaValidation),
    JsonDiscriminatorValue(ImportConfigType.Sftp)
], SftpImportConfig);
export { SftpImportConfig };
//# sourceMappingURL=sftpImportConfig.js.map